import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { X, ChevronDown } from 'lucide-react';
import { MACHINE_TYPES, getMachineLabel, type MachineCode } from '@/constants/machines';

interface MachineMultiSelectProps {
  /** Currently selected machine codes */
  value: string[];
  /** Callback when selection changes */
  onChange: (value: string[]) => void;
  /** Placeholder text when no value selected */
  placeholder?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Multi-select machine dropdown component.
 * Allows selecting multiple machines for few-shot examples.
 */
export function MachineMultiSelect({
  value,
  onChange,
  placeholder = 'Maschinen auswahlen...',
  disabled = false,
  className,
}: MachineMultiSelectProps) {
  const handleToggle = (machineCode: string) => {
    if (value.includes(machineCode)) {
      onChange(value.filter((v) => v !== machineCode));
    } else {
      onChange([...value, machineCode]);
    }
  };

  const handleRemove = (machineCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== machineCode));
  };

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            className={`
              w-full justify-between h-auto min-h-10 py-2
              border-emerald-400 bg-emerald-50/50
              hover:bg-emerald-50 hover:border-emerald-500
              focus:ring-emerald-500/20 focus:border-emerald-500
              transition-colors
            `}
          >
            {value.length === 0 ? (
              <span className="text-muted-foreground font-normal">{placeholder}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {value.map((code) => (
                  <Badge
                    key={code}
                    variant="secondary"
                    className="flex items-center gap-1 text-xs"
                  >
                    {getMachineLabel(code)}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={(e) => handleRemove(code, e)}
                    />
                  </Badge>
                ))}
              </div>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          {MACHINE_TYPES.map((machine) => (
            <DropdownMenuCheckboxItem
              key={machine.value}
              checked={value.includes(machine.value)}
              onCheckedChange={() => handleToggle(machine.value)}
            >
              {machine.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
